import { listUniverses, listUniversesByOrg, getUniverse, listOrgs, getOrg, listPartnerApprovals, submitPartnerApproval } from './chunk-LV3HULGJ.js';
import { createContext, useContext } from 'react';
import { jsx } from 'react/jsx-runtime';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

var SsupDbContext = createContext(null);
function SsupDataProvider({ db, children }) {
  return /* @__PURE__ */ jsx(SsupDbContext.Provider, { value: db, children });
}
function useSsupDb() {
  const db = useContext(SsupDbContext);
  if (!db) {
    throw new Error("useSsupDb must be used within a <SsupDataProvider db={...}>");
  }
  return db;
}

// src/react-hooks/keys.ts
var ssupKeys = {
  universes: {
    all: () => ["ssup", "universes", "all"],
    byOrg: (orgId) => ["ssup", "universes", "org", orgId],
    one: (universeId) => ["ssup", "universes", "id", universeId]
  },
  orgs: {
    all: () => ["ssup", "orgs", "all"],
    one: (orgId) => ["ssup", "orgs", "id", orgId]
  },
  approvals: {
    byUniverse: (universeId) => ["ssup", "approvals", universeId]
  }
};
function useUniverses() {
  const db = useSsupDb();
  return useQuery({ queryKey: ssupKeys.universes.all(), queryFn: () => listUniverses(db) });
}
function useUniversesByOrg(orgId) {
  const db = useSsupDb();
  return useQuery({
    queryKey: ssupKeys.universes.byOrg(orgId),
    queryFn: () => listUniversesByOrg(db, orgId),
    enabled: !!orgId
  });
}
function useUniverse(universeId) {
  const db = useSsupDb();
  return useQuery({
    queryKey: ssupKeys.universes.one(universeId ?? ""),
    queryFn: () => getUniverse(db, universeId),
    enabled: !!universeId
  });
}
function useOrgs() {
  const db = useSsupDb();
  return useQuery({ queryKey: ssupKeys.orgs.all(), queryFn: () => listOrgs(db) });
}
function useOrg(orgId) {
  const db = useSsupDb();
  return useQuery({
    queryKey: ssupKeys.orgs.one(orgId),
    queryFn: () => getOrg(db, orgId),
    enabled: !!orgId
  });
}
function useUniverseListing({
  isAdmin,
  orgId
}) {
  const db = useSsupDb();
  const universesQuery = useQuery({
    queryKey: isAdmin ? ssupKeys.universes.all() : ssupKeys.universes.byOrg(orgId),
    queryFn: () => isAdmin ? listUniverses(db) : listUniversesByOrg(db, orgId),
    enabled: isAdmin || !!orgId
  });
  const orgsQuery = useQuery({
    queryKey: isAdmin ? ssupKeys.orgs.all() : ssupKeys.orgs.one(orgId),
    queryFn: async () => {
      if (isAdmin) return listOrgs(db);
      const one = await getOrg(db, orgId);
      return one ? [one] : [];
    },
    enabled: isAdmin || !!orgId
  });
  const orgNames = Object.fromEntries(
    (orgsQuery.data ?? []).map((o) => [o.id, o.name])
  );
  return {
    universes: universesQuery.data ?? [],
    orgNames,
    isLoading: universesQuery.isLoading || orgsQuery.isLoading,
    error: universesQuery.error ?? orgsQuery.error
  };
}
function usePartnerApprovals(universeId) {
  const db = useSsupDb();
  return useQuery({
    queryKey: ssupKeys.approvals.byUniverse(universeId ?? ""),
    queryFn: () => listPartnerApprovals(db, universeId),
    enabled: !!universeId
  });
}
function useSubmitPartnerApproval() {
  const db = useSsupDb();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => submitPartnerApproval(db, input),
    onSuccess: (approval) => {
      void queryClient.invalidateQueries({
        queryKey: ssupKeys.approvals.byUniverse(approval.universe_id)
      });
      void queryClient.invalidateQueries({
        queryKey: ssupKeys.universes.one(approval.universe_id)
      });
    }
  });
}

export { SsupDataProvider, ssupKeys, useOrg, useOrgs, usePartnerApprovals, useSsupDb, useSubmitPartnerApproval, useUniverse, useUniverseListing, useUniverses, useUniversesByOrg };
//# sourceMappingURL=react.js.map
//# sourceMappingURL=react.js.map